import {
    AccountInfo,
    AuthenticationResult,
    EventMessage,
    EventType,
    IPublicClientApplication
} from '@azure/msal-browser';

import { createContext, useContext, useEffect, useState } from 'react';

import { appRoles } from '../../authConfig';
import { IdentityType } from './IdentityType';

export const IdentityContext = createContext<IdentityType>(IdentityType.None);

export const useIdentityContext = () => {
    return useContext(IdentityContext);
};

export const useIdentity = (pca: IPublicClientApplication) => {
    const [identity, setIdentity] = useState<IdentityType>(IdentityType.None);

    useEffect(() => {
        function setAccount(account: AccountInfo | null) {
            pca.setActiveAccount(account);

            if (account?.idTokenClaims == null) {
                setIdentity(IdentityType.None);
                return;
            }

            const roles = account.idTokenClaims.roles ?? [];

            const isDeveloper = roles.includes(appRoles.Developer);
            const isAdmin = roles.includes(appRoles.Admin);
            const isFinanceController = roles.includes(
                appRoles.FinanceController
            );

            if (isDeveloper) {
                setIdentity(IdentityType.Developer);
            } else if (isAdmin) {
                setIdentity(IdentityType.Admin);
            } else if (isFinanceController) {
                setIdentity(IdentityType.FinanceController);
            } else {
                setIdentity(IdentityType.User);
            }
        }

        function setFirstAccount() {
            const accounts = pca.getAllAccounts();
            setAccount(accounts.length > 0 ? accounts[0] : null);
        }

        setFirstAccount();

        pca.addEventCallback((event: EventMessage) => {
            if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
                const payload = event.payload as AuthenticationResult;
                const account = payload.account;
                setAccount(account);
            }

            if (event.eventType === EventType.LOGOUT_SUCCESS) {
                setFirstAccount();
            }
        });
    }, [pca]);

    return identity;
};
